import { Loading } from '@/core/ui/components/Loading';
import type { DelinquentItem } from '@yawi/types';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Clock, DollarSign, Users } from 'lucide-react';
import { useState } from 'react';
import { ReportExportButton } from '../components';
import { useDelinquentReport } from '../hooks';
import { formatCurrency } from '@yawi/infrastructure';

interface DelinquentReportPageProps {
  onBack: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const DelinquentRow = ({ item }: { item: DelinquentItem }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 px-4">
          <div className="font-medium text-slate-800">{item.unitName}</div>
          {item.tower && <div className="text-xs text-slate-400">Torre {item.tower}</div>}
        </td>
        {/* <td className="py-3 px-4">
          <div className="text-sm text-slate-700">{item.memberName}</div>
          {item.memberEmail && <div className="text-xs text-slate-400">{item.memberEmail}</div>}
          {item.memberPhone && <div className="text-xs text-slate-400">{item.memberPhone}</div>}
        </td> */}
        <td className="py-3 px-4 text-right font-semibold text-red-600">
          {formatCurrency(item.totalOwed)}
        </td>
        <td className="py-3 px-4 text-center">
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            {item.maxDaysOverdue} días
          </span>
        </td>
        <td className="py-3 px-4 text-center text-sm text-slate-600">
          {item.chargesCount}
        </td>
        <td className="py-3 px-4 text-center">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-slate-50 px-4 py-3">
            <div className="text-sm font-medium text-slate-600 mb-2">Detalle de cargos vencidos:</div>
            <div className="space-y-2">
              {item.charges.map((charge) => (
                <div key={charge.chargeId} className="flex justify-between items-center p-2 bg-white rounded border border-slate-200">
                  <div>
                    <div className="font-medium text-slate-700">{charge.concept}</div>
                    <div className="text-xs text-slate-400">
                      Vence: {formatDate(charge.dueDate)} | {charge.daysOverdue} días de retraso
                      {charge.period && ` | Periodo: ${charge.period}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-red-600">{formatCurrency(charge.balance)}</div>
                    {charge.balance !== charge.amount && (
                      <div className="text-xs text-slate-400">de {formatCurrency(charge.amount)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const DelinquentReportPage = ({ onBack }: DelinquentReportPageProps) => {
  const { report, loading, exporting, params, updateParams, exportReport } = useDelinquentReport();

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Reporte de Morosos</h2>
            <p className="text-slate-500 text-sm">Unidades con cargos vencidos</p>
          </div>
        </div>

        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Días mínimos de retraso
            </label>
            <input
              type="number"
              min="1"
              value={params.minDaysOverdue || 1}
              onChange={(e) => updateParams({ minDaysOverdue: parseInt(e.target.value) || 1, page: 1 })}
              className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Ordenar por
            </label>
            <select
              value={params.sortBy || 'daysOverdue'}
              onChange={(e) => updateParams({ sortBy: e.target.value as 'amount' | 'daysOverdue', page: 1 })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="daysOverdue">Días de retraso</option>
              <option value="amount">Monto adeudado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Orden
            </label>
            <select
              value={params.sortOrder || 'DESC'}
              onChange={(e) => updateParams({ sortOrder: e.target.value as 'ASC' | 'DESC', page: 1 })}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="DESC">Mayor a menor</option>
              <option value="ASC">Menor a mayor</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Loading />
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users size={18} className="text-red-600" />
                <span className="text-sm text-slate-600">Total Morosos</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{report.summary.totalDelinquents}</p>
            </div>

            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={18} className="text-amber-600" />
                <span className="text-sm text-slate-600">Total Adeudado</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(report.summary.totalAmountOwed)}</p>
            </div>

            <div className="bg-orange-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={18} className="text-orange-600" />
                <span className="text-sm text-slate-600">Promedio Días</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">{Math.round(report.summary.averageDaysOverdue)}</p>
            </div>

            <div className="bg-rose-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-rose-600" />
                <span className="text-sm text-slate-600">Máximo Adeudo</span>
              </div>
              <p className="text-2xl font-bold text-rose-600">{formatCurrency(report.summary.maxAmountOwed)}</p>
            </div>
          </div>

          {/* Table */}
          {report.data.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <AlertTriangle size={48} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">No hay unidades morosas con los filtros seleccionados</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Unidad</th>
                      {/* <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Propietario</th> */}
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Total Adeudado</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Días Retraso</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Cargos</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data.map((item) => (
                      <DelinquentRow key={item.unitId} item={item} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {report.meta.totalPages > 1 && (
                <div className="flex justify-between items-center p-4 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Mostrando {(report.meta.page - 1) * report.meta.limit + 1} - {Math.min(report.meta.page * report.meta.limit, report.meta.total)} de {report.meta.total}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateParams({ page: (params.page || 1) - 1 })}
                      disabled={!report.meta.hasPreviousPage}
                      className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => updateParams({ page: (params.page || 1) + 1 })}
                      disabled={!report.meta.hasNextPage}
                      className="px-3 py-1 border border-slate-200 rounded text-sm disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-slate-500">
          No se pudo cargar el reporte
        </div>
      )}
    </div>
  );
};
